import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronUp, UserCog, Users, UserCircle } from 'lucide-react';
import { AVP, Director, LoanOfficerWithContext } from '../types';
import { formatCurrency, formatCurrencyExact, formatNumber } from '../utils/formatters';
export type ViewMode = 'avp' | 'director' | 'lo';
interface PerformanceTableProps {
  avps: AVP[];
  directors: Director[];
  loanOfficers: LoanOfficerWithContext[];
  view?: ViewMode;
  onViewChange?: (v: ViewMode) => void;
}
type SortDir = 'asc' | 'desc';
// Unified row shape across views — fields used per view vary
interface Row {
  id: string;
  initials: string;
  name: string;
  reportsTo: string; // for AVP: '—'; for Director: AVP name; for LO: Director · AVP
  division: string;
  loCount?: number;
  loansClosed: number;
  loanVolume: number;
  totalRevenue: number;
  avgRevenue: number;
}
export function PerformanceTable({
  avps,
  directors,
  loanOfficers,
  view: controlledView,
  onViewChange
}: PerformanceTableProps) {
  const [internalView, setInternalView] = useState<ViewMode>('avp');
  const view = controlledView ?? internalView;
  const setView = (v: ViewMode) => {
    setInternalView(v);
    onViewChange?.(v);
  };
  const [searchTerm, setSearchTerm] = useState('');
  // Each view has a different default ranking metric
  const defaultSortField = view === 'lo' ? 'totalRevenue' : 'avgRevenue';
  // SortField is a string for flexibility; runtime guarantees it maps to a real column
  const [sortField, setSortField] = useState<string>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDir>('desc');
  // Reset sort when switching views so the right ranking metric leads
  useEffect(() => {
    setSortField(defaultSortField);
    setSortDirection('desc');
  }, [view, defaultSortField]);
  const rows: Row[] = useMemo(() => {
    if (view === 'avp') {
      return avps.map(a => ({
        id: a.id,
        initials: a.initials,
        name: a.name,
        reportsTo: '—',
        division: a.division,
        loCount: a.loCount,
        loansClosed: a.loansClosed,
        loanVolume: a.loanVolume,
        totalRevenue: a.totalRevenue,
        avgRevenue: a.avgRevenue
      }));
    }
    if (view === 'director') {
      return directors.map(d => ({
        id: d.id,
        initials: d.initials,
        name: d.name,
        reportsTo: d.avpName ?? '—',
        division: d.division ?? '—',
        loCount: d.loCount,
        loansClosed: d.loansClosed,
        loanVolume: d.loanVolume,
        totalRevenue: d.totalRevenue,
        avgRevenue: d.avgRevenue
      }));
    }
    return loanOfficers.map(lo => ({
      id: lo.id,
      initials: lo.initials,
      name: lo.name,
      reportsTo: lo.directorName,
      division: lo.division,
      loansClosed: lo.loansClosed,
      loanVolume: lo.loanVolume,
      totalRevenue: lo.totalRevenue,
      avgRevenue: lo.avgRevenue
    }));
  }, [view, avps, directors, loanOfficers]);
  // Rank map keyed by the active ranking metric
  const rankByMetric = useMemo(() => {
    const m = new Map<string, number>();
    [...rows].sort((a, b) => (b as any)[defaultSortField] - (a as any)[defaultSortField]).forEach((r, idx) => m.set(r.id, idx + 1));
    return m;
  }, [rows, defaultSortField]);
  const filtered = rows.filter(r => {
    const q = searchTerm.toLowerCase();
    return r.name.toLowerCase().includes(q) || r.division.toLowerCase().includes(q) || r.reportsTo.toLowerCase().includes(q);
  });
  const sorted = [...filtered].sort((a, b) => {
    const aValue = sortField === 'rank' ? rankByMetric.get(a.id)! : (a as any)[sortField];
    const bValue = sortField === 'rank' ? rankByMetric.get(b.id)! : (b as any)[sortField];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const SortIcon = ({
    field
  }: {
    field: string;
  }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" data-id="element-434" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 text-indigo-400" data-id="element-435" /> : <ChevronDown className="w-4 h-4 text-indigo-400" data-id="element-436" />;
  };
  const nameLabel = view === 'avp' ? 'Leader' : view === 'director' ? 'Director' : 'Officer';
  const showReportsTo = view !== 'avp';
  const showLoCount = view !== 'lo';
  // Total columns for empty colspan
  const colCount = 1 /* rank */ + 1 /* name */ + (showReportsTo ? 1 : 0) + (showLoCount ? 1 : 0) + 1 /* loans */ + 1 /* volume */ + 1 /* total */ + 1; /* avg */
  return <section className="mb-12" data-id="element-437">
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4" data-id="element-438">
        <h2 className="text-xl font-semibold text-white" data-id="element-439">
          Detailed Performance
        </h2>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto" data-id="element-440">
          {/* View toggle */}
          <div className="glass-panel rounded-lg p-1 flex text-sm" data-id="element-441">
            {([{
            id: 'avp',
            label: 'AVPs',
            Icon: UserCog
          }, {
            id: 'director',
            label: 'Directors',
            Icon: Users
          }, {
            id: 'lo',
            label: 'Loan Officers',
            Icon: UserCircle
          }] as {
            id: ViewMode;
            label: string;
            Icon: typeof UserCog;
          }[]).map(v => <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${view === v.id ? 'bg-indigo-500/20 text-indigo-300 font-medium' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-442">
                <v.Icon className="w-3.5 h-3.5" data-id="element-443" />
                {v.label}
              </button>)}
          </div>

          <div className="relative flex-1 md:w-64" data-id="element-444">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-445" />
            <input type="text" placeholder={`Search ${nameLabel.toLowerCase()}s...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" data-id="element-446" />
          </div>
        </div>
      </div>

      <div className="glass-panel rounded-3xl overflow-hidden" data-id="element-447">
        <div className="overflow-x-auto" data-id="element-448">
          <table className="w-full text-left border-collapse" data-id="element-449">
            <thead data-id="element-450">
              <tr className="border-b border-white/10 bg-white/[0.02]" data-id="element-451">
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('rank')} data-id="element-452">
                  <div className="flex items-center gap-1" data-id="element-453">
                    Rank <SortIcon field="rank" data-id="element-454" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')} data-id="element-455">
                  <div className="flex items-center gap-1" data-id="element-456">
                    {nameLabel} <SortIcon field="name" data-id="element-457" />
                  </div>
                </th>
                {showReportsTo && <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('reportsTo')} data-id="element-458">
                    <div className="flex items-center gap-1" data-id="element-459">
                      Reports To <SortIcon field="reportsTo" data-id="element-460" />
                    </div>
                  </th>}
                {showLoCount && <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('loCount')} data-id="element-461">
                    <div className="flex items-center gap-1 justify-end" data-id="element-462">
                      LO Count <SortIcon field="loCount" data-id="element-463" />
                    </div>
                  </th>}
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('loansClosed')} data-id="element-464">
                  <div className="flex items-center gap-1 justify-end" data-id="element-465">
                    Loans <SortIcon field="loansClosed" data-id="element-466" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('loanVolume')} data-id="element-467">
                  <div className="flex items-center gap-1 justify-end" data-id="element-468">
                    Volume <SortIcon field="loanVolume" data-id="element-469" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('totalRevenue')} data-id="element-470">
                  <div className="flex items-center gap-1 justify-end whitespace-nowrap" data-id="element-471">
                    Total Commissions (Wire) <SortIcon field="totalRevenue" data-id="element-472" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('avgRevenue')} data-id="element-473">
                  <div className="flex items-center gap-1 justify-end" data-id="element-474">
                    Avg Revenue <SortIcon field="avgRevenue" data-id="element-475" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-476">
              {sorted.map(r => {
              const rank = rankByMetric.get(r.id) ?? 0;
              const isTopMetric = (field: string) => field === defaultSortField;
              return <tr key={r.id} className="hover:bg-white/[0.02] transition-colors group" data-id="element-477">
                    <td className="py-4 px-6" data-id="element-478">
                      <span className={`w-6 text-center font-semibold ${rank <= 3 ? 'text-yellow-400' : 'text-slate-300'}`} data-id="element-479">
                        {rank}
                      </span>
                    </td>
                    <td className="py-4 px-6" data-id="element-480">
                      <div className="flex items-center gap-3" data-id="element-481">
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-semibold" data-id="element-482">
                          {r.initials}
                        </div>
                        <p className="text-sm font-medium text-white" data-id="element-483">
                          {r.name}
                        </p>
                      </div>
                    </td>
                    {showReportsTo && <td className="py-4 px-6 text-sm text-slate-300" data-id="element-484">
                        {r.reportsTo}
                      </td>}
                    {showLoCount && <td className="py-4 px-6 text-sm text-slate-300 text-right" data-id="element-485">
                        {r.loCount ?? '—'}
                      </td>}
                    <td className="py-4 px-6 text-sm text-slate-300 text-right" data-id="element-486">
                      {formatNumber(r.loansClosed)}
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-300 text-right whitespace-nowrap" data-id="element-487">
                      {formatCurrency(r.loanVolume, true)}
                    </td>
                    <td className={`py-4 px-6 text-sm font-medium text-right whitespace-nowrap ${isTopMetric('totalRevenue') ? 'text-emerald-400' : 'text-white'}`} data-id="element-488">
                      {formatCurrencyExact(r.totalRevenue)}
                    </td>
                    <td className={`py-4 px-6 text-sm font-medium text-right whitespace-nowrap ${isTopMetric('avgRevenue') ? 'text-emerald-400' : 'text-white'}`} data-id="element-489">
                      {formatCurrencyExact(r.avgRevenue)}
                    </td>
                  </tr>;
            })}
              {sorted.length === 0 && <tr data-id="element-490">
                  <td colSpan={colCount} className="py-12 text-center text-slate-500 text-sm" data-id="element-491">
                    No {nameLabel.toLowerCase()}s found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}